// docs/114 MT4 — how each thing on the field is drawn: an Oryx frame where the sheets have one
// (a second frame to flicker between while it burns or bubbles, another for it unlit), a tint and a
// small mark to tell kinds that share a frame apart, or pixel-art of its own (`textures.ts`); and
// the light it throws while lit.
import type { ThingKind } from '../../core/sim/types';
import { drawnKey } from './textures';

export interface ThingLook {
  /** Atlas frame (`world_…` or `item_…`), or a drawn texture key. */
  frame: string;
  /** A second frame to alternate with (a brazier's flames, a cauldron's bubbles). */
  frameB?: string;
  /** Frame while unlit, for things that start lit (a torch put out). */
  dark?: string;
  tint?: number;
  /** A mark from `textures.ts` laid over the frame. */
  mark?: string;
  /** Share of a square it fills (item frames are small). */
  scale?: number;
  /** Hangs overhead: drawn high in its square. */
  hangs?: boolean;
  /** Its light while lit: colour and radius in squares. */
  light?: { color: number; radius: number };
}

const FIRE = { color: 0xffa040, radius: 3 };
const CANDLE = { color: 0xffd080, radius: 2 };

const OLD_LOOK = {
  OilBarrel: { frame: 'world_0268', mark: 'drip' },
  PowderKeg: { frame: 'world_0268', tint: 0x9a8a7a, mark: 'keg' },
  WaterBarrel: { frame: 'world_0269', mark: 'water' },
  AleCask: { frame: 'world_0268', tint: 0xe0c080, mark: 'ale' },
  AcidVat: { frame: 'world_0374', frameB: 'world_0375', tint: 0xb0ffa0, mark: 'acid' },
  BrewCauldron: { frame: 'world_0374', frameB: 'world_0375', tint: 0xd0b0ff, mark: 'brew' },
  HotOilVat: { frame: 'world_0374', frameB: 'world_0375', tint: 0xffc890, mark: 'oil', light: FIRE },
  Brazier: { frame: 'world_0099', frameB: 'world_0100', light: FIRE },
  Campfire: { frame: 'world_0097', frameB: 'world_0098', light: FIRE },
  Torch: { frame: 'item_153', dark: 'item_152', scale: 0.8, light: FIRE },
  Lantern: { frame: 'item_148', scale: 0.7, light: CANDLE },
  Candles: { frame: 'world_0323', frameB: 'world_0324', light: CANDLE },
  Chandelier: { frame: drawnKey('Chandelier'), dark: drawnKey('ChandelierDark'), hangs: true, light: CANDLE },
  Boulder: { frame: 'world_0164' },
  Log: { frame: drawnKey('Log') },
  Crate: { frame: 'world_0435' },
  Haybale: { frame: drawnKey('Haybale') },
  Woodpile: { frame: drawnKey('Woodpile') },
  Bookshelf: { frame: 'world_0317' },
  Banner: { frame: 'world_1546' },
  Beehive: { frame: drawnKey('Beehive') },
  WaspNest: { frame: drawnKey('WaspNest') },
  Puffballs: { frame: 'item_094', scale: 0.8 },
  MarshVent: { frame: drawnKey('MarshVent') },
  SteamVent: { frame: drawnKey('SteamVent') },
  Icicles: { frame: drawnKey('Icicles'), hangs: true },
  IcePillar: { frame: drawnKey('IcePillar') },
  HolyFont: { frame: 'world_0486', tint: 0xd8f0ff },
  Well: { frame: 'world_0548' },
  Trough: { frame: drawnKey('Trough') },
  AlchemyBench: { frame: drawnKey('AlchemyBench') },
  FlourSacks: { frame: 'item_151', tint: 0xf4ecd8, scale: 0.85 },
  MineCart: { frame: drawnKey('MineCart') },
  RottenBeam: { frame: drawnKey('RottenBeam') },
  FireworksCrate: { frame: 'world_0436', mark: 'fireworks' },
  TarBarrel: { frame: 'world_0268', tint: 0x5a5050, mark: 'drip' },
  Bell: { frame: drawnKey('Bell') },
  Gong: { frame: drawnKey('Gong') },
  EggSac: { frame: drawnKey('EggSac') },
  DeadTree: { frame: 'world_0276' },
  Snowman: { frame: drawnKey('Snowman') },
  Scarecrow: { frame: drawnKey('Scarecrow') },
  BoneHeap: { frame: 'world_0094' },
  Tent: { frame: drawnKey('Tent') },
  Cart: { frame: drawnKey('Cart') },
  LightningRod: { frame: drawnKey('LightningRod') },
  BlightFont: { frame: 'world_0486', tint: 0x9a70c0 },
  BrineTub: { frame: 'world_0269', tint: 0xc0d0dc, mark: 'brine' },
  Hearth: { frame: drawnKey('Hearth'), light: FIRE },
  Anvil: { frame: drawnKey('Anvil') },
} satisfies Partial<Record<ThingKind, ThingLook>>;

/**
 * docs/189: the everyday things are drawn from the objects atlas (`objectArt.ts`); this is only their Oryx
 * stand-in, for `?oryx=1` and before the atlas loads — the nearest frame the sheets have, and the light a lamp throws.
 */
const CRATE = 'world_0435', BARREL = 'world_0268';
const NEW_LOOK: Record<Exclude<ThingKind, keyof typeof OLD_LOOK>, ThingLook> = {
  Wagon: { frame: drawnKey('Cart') }, CoveredWagon: { frame: drawnKey('Cart') }, HayCart: { frame: drawnKey('Cart'), tint: 0xf0d890 },
  Wheelbarrow: { frame: drawnKey('Cart'), scale: 0.8 }, Rowboat: { frame: drawnKey('Log') }, BarrelRack: { frame: BARREL },
  CrateStack: { frame: CRATE }, Bucket: { frame: 'world_0269', scale: 0.6 }, ButterChurn: { frame: BARREL, scale: 0.8 },
  Amphorae: { frame: 'item_151', scale: 0.8, tint: 0xd08a5a }, WineRack: { frame: 'world_0317', tint: 0x8aa070 },
  FoodBasket: { frame: 'item_151', scale: 0.7, tint: 0xd8b070 }, CheeseWheels: { frame: 'item_151', scale: 0.7, tint: 0xf0d060 },
  MeatRack: { frame: drawnKey('Woodpile') }, Bed: { frame: CRATE, tint: 0xd06060 }, BunkBed: { frame: CRATE, tint: 0xa08060 },
  Table: { frame: CRATE }, TableTipped: { frame: CRATE }, FeastTable: { frame: CRATE }, LongTable: { frame: CRATE },
  Chair: { frame: CRATE, scale: 0.7 }, Bench: { frame: CRATE, scale: 0.8 }, Dresser: { frame: 'world_0317' },
  Wardrobe: { frame: 'world_0317' }, DishDresser: { frame: 'world_0317' }, Washtub: { frame: 'world_0269' },
  SpinningWheel: { frame: CRATE, scale: 0.8 }, Loom: { frame: CRATE }, Rug: { frame: 'world_1546', tint: 0xa04040 },
  Tapestry: { frame: 'world_1546' }, Painting: { frame: 'world_1546', tint: 0xd0b070 }, Crest: { frame: 'world_1546', tint: 0x8090c0 },
  Trophy: { frame: 'world_0094' }, ToolRack: { frame: CRATE, scale: 0.7 },
  Stewpot: { frame: 'world_0374', frameB: 'world_0375', light: FIRE }, BreadOven: { frame: drawnKey('Hearth'), light: FIRE },
  ArmourStand: { frame: drawnKey('Anvil') }, Lectern: { frame: 'world_0317', scale: 0.8 }, Pew: { frame: CRATE },
  Altar: { frame: 'world_0486' }, Coffin: { frame: CRATE, tint: 0x806050 }, Forge: { frame: drawnKey('Hearth'), light: FIRE },
  Grindstone: { frame: drawnKey('Anvil') }, WeaponRack: { frame: 'world_0317', tint: 0xb0b0b0 }, Workbench: { frame: CRATE },
  Rake: { frame: drawnKey('Woodpile'), scale: 0.6 }, Haystack: { frame: drawnKey('Haybale') }, ChickenCoop: { frame: CRATE },
  Dungheap: { frame: 'world_0094', tint: 0x6a4a2a }, Plough: { frame: drawnKey('Anvil') }, ChoppingBlock: { frame: drawnKey('Woodpile') },
  Bedroll: { frame: 'world_1546', scale: 0.7, tint: 0x806040 }, LeanTo: { frame: drawnKey('Tent') }, SupplyPile: { frame: CRATE },
  TrainingDummy: { frame: drawnKey('Scarecrow') }, ArcheryTarget: { frame: drawnKey('Scarecrow') },
  RoastSpit: { frame: 'world_0097', frameB: 'world_0098', light: FIRE }, Pavilion: { frame: drawnKey('Tent') },
  Signpost: { frame: drawnKey('Scarecrow') }, LampPost: { frame: 'item_148', scale: 0.7, light: CANDLE },
  MarketStall: { frame: CRATE }, Fountain: { frame: 'world_0548' }, Stocks: { frame: CRATE }, Barricade: { frame: CRATE },
  WarDrum: { frame: BARREL, scale: 0.8 }, Cage: { frame: drawnKey('Anvil') }, SignalPyre: { frame: drawnKey('Woodpile'), light: { color: 0xffa040, radius: 5 } },
  Nets: { frame: 'world_0094', tint: 0x9a7a50 }, RopeCoil: { frame: 'world_0094', tint: 0xc0a070 }, LobsterPots: { frame: CRATE },
  Anchor: { frame: drawnKey('Anvil') }, Gravestone: { frame: 'world_0094', tint: 0x9a9a9a }, FlagPole: { frame: 'world_1546' },
  WritingDesk: { frame: CRATE },
};

export const THING_LOOK: Record<ThingKind, ThingLook> = { ...OLD_LOOK, ...NEW_LOOK };

/** Things that shine when lit (their flame or candles), not things that are merely on fire. */
export const LIGHTS: readonly ThingKind[] = ['Brazier', 'Campfire', 'Torch', 'Lantern', 'Candles', 'Chandelier', 'Hearth', 'HotOilVat',
  'Stewpot', 'BreadOven', 'Forge', 'RoastSpit', 'LampPost', 'SignalPyre'];

/** Things that bubble or flicker whether lit or not. */
export const ALWAYS_ANIMATED: readonly ThingKind[] = ['AcidVat', 'BrewCauldron'];

/** Fallback when a frame is missing from the atlases: a drawn shape that still reads. */
export const FALLBACK: Partial<Record<ThingKind, string>> = {
  HolyFont: drawnKey('HolyFont'),
  BlightFont: drawnKey('BlightFont'),
};
